import _ from "lodash";
import React from "react";

import {CustomerMenuItem} from "../CustomerMenuItem";
import {buildDepotAccountSteps, getDepotAccountStepUID} from "../../../RiskProfiling/components/servicesV2/mock_account_data_trading";
import {
  buildPortfolioClearingAccountBalancingQuestion,
  buildPortfolioClearingAccountQuestion,
  getPortfolioClearingAccountOptionQuestionUID, validateCashAccountAsBank,
} from "../../../RiskProfiling/utils";
import {
  ACCOUNT_DATA_SUCCESS_MSG,
  CLEARING_ACCOUNT_BALANCING_QUESTION_UID,
  CLEARING_ACCOUNT_OPTION_QUESTION_UID,
  CLEARING_ACCOUNT_QUESTION_UID,
  ORIGIN_OF_ASSETS_UID, QUESTION_TYPE
} from "../../../RiskProfiling/constants";
import {portfolioTransactionAmount} from "../../utils";
import {TRANSACTION_TYPE_VALUES} from "../../constants";
import {PortfolioHandlerResource} from "../../../../utils/api";
import {
  CashAccountCard
} from "../../../RiskProfiling/components/StepContent/components/step/CustomerDataSteps/DepotAccountStepV2";
import {RadioQuestionMethods} from "../../../RiskProfiling/components/StepContent/components/question/RadioQuestion";

export class CustomerAccountMenuItemV2 extends CustomerMenuItem {
  constructor(uid, name, customer_id, getPrevStepAnswer, validateAccountFunc) {
    super(uid, name, customer_id, getPrevStepAnswer, validateAccountFunc);
    this._validateAccountStep = undefined;
    this._success_body = ACCOUNT_DATA_SUCCESS_MSG
  }

  _buildSteps(customer) {
    const originOfAssets = this.isSwitchSellFlow ? [] : this._getAvailableOriginOfAssets();
    const portfolios = this._getPrevStepAnswer('trade', 'trade-step', "transactions", true) || [];
    return buildDepotAccountSteps(portfolios, originOfAssets);
  }

  /**
   * Override: populate clearing account questions per-depot step.
   * Each depot step has its own clearing_account, clearing_account_option,
   * and clearing_account_balancing questions scoped to that depot.
   */
  async setClearingAccountQuestionOptions() {
    const portfolios = this._getPrevStepAnswer('trade', 'trade-step', "transactions", true) || [];

    if (_.isEmpty(portfolios)) return;

    const finData = await PortfolioHandlerResource.getDepotFinancialData(
      this.customer_id, portfolios.map(p => p.portfolioId));

    this._portfoliosWithClearingAccounts = _.keyBy(
      _.get(finData, 'financial.data.0.data.portfolios', []),
      'id'
    );

    // Populate each depot step with its clearing account questions
    this.portfolios.forEach(portfolio => {
      const stepUID = getDepotAccountStepUID(portfolio);
      const step = this.getStep(stepUID);

      const clearingAccountQuestion = _.find(step.question,
        (q) => q.uid === CLEARING_ACCOUNT_QUESTION_UID);
      const clearingAccountOptionQuestion = _.find(step.question,
        (q) => q.uid === CLEARING_ACCOUNT_OPTION_QUESTION_UID);
      const clearingAccountBalancingQuestion = _.find(step.question,
        (q) => q.uid === CLEARING_ACCOUNT_BALANCING_QUESTION_UID);

      if (!clearingAccountQuestion || !clearingAccountOptionQuestion || !clearingAccountBalancingQuestion) {
        return;
      }

      if (!_.isEmpty(portfolio.clearing_account) &&
          (portfolio.orderClearingAccountAllowed || portfolio.orderClearingAccountToBankAccountAllowed)) {

        const portfolioClearingAccountQuestion = buildPortfolioClearingAccountQuestion(portfolio);
        portfolioClearingAccountQuestion._ignore = true;
        const ptfSingleBuyAmount = portfolioTransactionAmount(portfolio, TRANSACTION_TYPE_VALUES.BUY);
        const asBankAccount = !portfolio.orderClearingAccountAllowed;

        // Build clearing account card choices directly (no select dropdown, no isValid referencing REF_ACCOUNT_STEP_ID)
        const choices = Object.values(portfolio.clearing_account).map((cAccount) => {
          const notEnoughMoney = cAccount.value < ptfSingleBuyAmount;

          return {
            id: cAccount.id,
            uid: String(cAccount.id),
            notEnoughMoney: notEnoughMoney,
            text: <CashAccountCard
              account={cAccount}
              portfolioName={portfolio.data.name}
              notEnoughMoney={notEnoughMoney}
              asBankAccount={asBankAccount}
              logo={portfolio.logo}
            />,
            clearing_account: {
              account_number: cAccount.account_number,
              iban: cAccount.iban,
              value: cAccount.value,
              value_date: cAccount.value_date
            }
          }
        });

        const clearingAccountCardQuestion = {
          uid: getPortfolioClearingAccountOptionQuestionUID(portfolio),
          optional: true,
          type: QUESTION_TYPE.RADIO_CARDS,
          question_text: 'Verrechnungskonto',
          config: { choices }
        };

        if(asBankAccount){
          clearingAccountCardQuestion.isValid = function (step, afterChange, service) {
            clearingAccountCardQuestion.error = null;

            if(RadioQuestionMethods.valid(clearingAccountCardQuestion)){
              validateCashAccountAsBank(clearingAccountCardQuestion, portfolio);
            }

            return _.isNil(clearingAccountCardQuestion.error);
          }
        }

        if (portfolio.orderClearingAccountAllowed && portfolio.clearingAccountBalancingAllowed && ptfSingleBuyAmount > 0) {
          if (_.some(Object.values(portfolio.clearing_account), (cAccount) => cAccount.value < ptfSingleBuyAmount)) {
            clearingAccountBalancingQuestion.question.push(buildPortfolioClearingAccountBalancingQuestion(portfolio));
          }
        }

        clearingAccountQuestion.question.push(portfolioClearingAccountQuestion);
        clearingAccountOptionQuestion.question.push(clearingAccountCardQuestion);
      }
    });
  }

  // Not needed — tab toggle in DepotAccountStepV2 handles bank_account optional/disabled state
  validateBankAccountRequirements() {}

  makeCustomerReferenceOptional() {
    const callback = (question) => {
      question.optional = true;
      if(question.hasOwnProperty('required_check')){
        question.required_check = false;
      }
      if (question.hasOwnProperty('question') && question.question.length) {
        question.question.forEach(subQuestion => {
          callback(subQuestion)
        });
      }
    };

    // Make all per-depot account steps optional
    const portfolios = this._getPrevStepAnswer('trade', 'trade-step', "transactions", true) || [];
    portfolios.forEach(portfolio => {
      this.__handleStepQuestions(getDepotAccountStepUID(portfolio), callback);
    });

    // Origin of assets step
    this.__handleStepQuestions(ORIGIN_OF_ASSETS_UID, callback);
  }

  getStepsAnswers(validate, portfolioId) {
    const data = super.getStepsAnswers(validate, portfolioId);

    return {
      client_details: data,
    }
  }
}
